import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, Grid, Typography, Button, Box, 
  Paper, Divider, Rating, Chip, ImageList, ImageListItem, Dialog, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, TextField
} from '@mui/material';
import { db } from '../config/firebase.js';
import { doc, getDoc } from 'firebase/firestore';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SpeedIcon from '@mui/icons-material/Speed';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import { Close as CloseIcon } from '@mui/icons-material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { motion } from 'framer-motion';
import CarCard from '../components/CarCard';
import { vehicles } from '../data/vehicles'; // Create this file to store vehicle data
import dayjs from 'dayjs';
import PaymentModal from '../components/PaymentModal';

// Keep the animation variants
const fadeInUpVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

function VehicleDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const vehicleDoc = await getDoc(doc(db, 'vehicles', id));
        if (vehicleDoc.exists()) {
          setVehicle({ id: vehicleDoc.id, ...vehicleDoc.data() });
        } else {
          console.error('Vehicle not found');
        }
      } catch (error) {
        console.error('Error fetching vehicle:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicle();
  }, [id]);

  useEffect(() => {
    if (dateRange[0] && dateRange[1] && vehicle) {
      const start = dayjs(dateRange[0]);
      const end = dayjs(dateRange[1]);
      const days = end.diff(start, 'day') + 1; // Including both start and end days
      const total = days * vehicle.price;
      setTotalAmount(total);
    }
  }, [dateRange, vehicle]);

  if (loading) return <div>Loading...</div>;
  if (!vehicle) return <div>Vehicle not found</div>;

  // Get similar vehicles
  const similarVehicles = vehicles
    .filter(v => v.type === vehicle.type && v.id !== vehicle.id)
    .slice(0, 3);

  const handleBookNow = () => {
    if (!dateRange[0] || !dateRange[1]) {
      alert('Please select rental dates');
      return;
    }
    setShowPayment(true);
  };

  const handlePaymentComplete = (paymentDetails) => {
    // Handle successful payment
    console.log('Payment completed:', paymentDetails);
    navigate('/booking-confirmation');
  };

  // Vehicle images (you'll need to add these to your data)
  const vehicleImages = [
    vehicle.image,
    `/vehicles/${vehicle.id}/image2.jpg`,
    `/vehicles/${vehicle.id}/image3.jpg`,
    `/vehicles/${vehicle.id}/image4.jpg`,
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Image Gallery */}
          <Grid item xs={12} md={7}>
            <Box sx={{ position: 'relative' }}>
              <motion.img
                src={vehicle.image}
                alt={vehicle.name}
                style={{ 
                  width: '100%',
                  height: '400px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  cursor: 'zoom-in',
                }}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedImage(vehicle.image)}
              />
              <ImageList cols={4} gap={8} sx={{ mt: 2 }}>
                {vehicleImages.map((img, index) => (
                  <ImageListItem 
                    key={index}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 },
                    }}
                    onClick={() => setSelectedImage(img)}
                  >
                    <img
                      src={img}
                      alt={`${vehicle.name} view ${index + 1}`}
                      style={{ 
                        height: '80px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          </Grid>

          {/* Details Section */}
          <Grid item xs={12} md={5}>
            <Typography variant="h4" gutterBottom>
              {vehicle.name}
            </Typography>
            
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Rating value={vehicle.ratings.average} readOnly precision={0.5} />
              <Typography>({vehicle.ratings.count} reviews)</Typography>
            </Box>

            <Typography variant="h5" color="primary" gutterBottom>
              ₹{vehicle.rentPerHour}/hour
            </Typography>

            <Box mb={3}>
              <Typography variant="subtitle1" gutterBottom>
                Brand: {vehicle.brand}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Model: {vehicle.model}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Type: {vehicle.type.charAt(0).toUpperCase() + vehicle.type.slice(1)}
              </Typography>
            </Box>

            {/* Specifications */}
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Specifications
              </Typography>
              <Grid container spacing={2}>
                {vehicle.type === 'electric' ? (
                  <>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <BatteryChargingFullIcon />
                        <Typography>Range: {vehicle.specifications.range}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>
                        Charging Time: {vehicle.charging?.chargingTime}
                      </Typography>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocalGasStationIcon />
                      <Typography>
                        {vehicle.specifications.fuelType || vehicle.specifications.engineSize}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SpeedIcon />
                    <Typography>Power: {vehicle.specifications.power}</Typography>
                  </Box>
                </Grid>
                {vehicle.specifications.seatingCapacity && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AirlineSeatReclineNormalIcon />
                      <Typography>
                        Seats: {vehicle.specifications.seatingCapacity}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Features */}
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Features
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {vehicle.features.map((feature, index) => (
                  <Chip key={index} label={feature} />
                ))}
              </Box>
            </Paper>

            {/* Location and Insurance */}
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <LocationOnIcon />
                <Typography>Location: {vehicle.location}</Typography>
              </Box>
              <Typography>
                Insurance: {vehicle.insurance.coverage}
                {vehicle.insurance.included && ' (Included)'}
              </Typography>
            </Paper>

            {/* Booking Calendar */}
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Select Rental Dates
              </Typography>
              <DateRangePicker
                startText="Pick-up"
                endText="Drop-off"
                value={dateRange}
                onChange={(newValue) => setDateRange(newValue)}
                renderInput={(startProps, endProps) => (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField {...startProps} />
                    <TextField {...endProps} />
                  </Box>
                )}
              />

              {/* Price Breakdown */}
              {dateRange[0] && dateRange[1] && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1">
                    Price Breakdown
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell>Duration</TableCell>
                          <TableCell align="right">
                            {dayjs(dateRange[1]).diff(dayjs(dateRange[0]), 'day') + 1} days
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Rate per day</TableCell>
                          <TableCell align="right">₹{vehicle.price}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Typography variant="subtitle1">Total</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle1">₹{totalAmount}</Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mt: 2 }}
                    onClick={handleBookNow}
                  >
                    Book Now
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Similar Vehicles */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="h4" gutterBottom>
            Similar Vehicles
          </Typography>
          <Grid container spacing={3}>
            {similarVehicles.map((vehicle) => (
              <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                <motion.div
                  variants={fadeInUpVariant}
                  initial="hidden"
                  animate="visible"
                  transition={{ duration: 0.5 }}
                >
                  <CarCard vehicle={vehicle} />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Image Zoom Dialog */}
        <Dialog
          open={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          maxWidth="lg"
          fullWidth
        >
          <IconButton
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
            }}
            onClick={() => setSelectedImage(null)}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={selectedImage}
            alt={vehicle.name}
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '90vh',
              objectFit: 'contain',
            }}
          />
        </Dialog>

        <PaymentModal
          open={showPayment}
          onClose={() => setShowPayment(false)}
          bookingDetails={{
            vehicleName: vehicle.name,
            totalAmount: totalAmount,
            vehicleId: vehicle.id,
            startDate: dateRange[0]?.format('YYYY-MM-DD'),
            endDate: dateRange[1]?.format('YYYY-MM-DD')
          }}
          onPaymentComplete={handlePaymentComplete}
        />
      </Container>
    </LocalizationProvider>
  );
}

export default VehicleDetails; 